import { getState, setState } from '../search_results/state'

/**
 * Shared utility for managing search filter annotations
 * Handles both overlay shape annotations and drawn shape annotations
 * Used by both old and new filter paths
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Gets a deep copy of the current annotations
 * @returns {Array} Deep copy of annotations array
 */
function getAnnotations() {
  return JSON.parse(JSON.stringify(getState('annotations')));
}

/**
 * Updates both state and sessionStorage with new annotations
 * @param {Array} annotations - The annotations array to save
 */
function updateAnnotations(annotations) {
  setState('annotations', annotations);
  sessionStorage.setItem('annotations', JSON.stringify(annotations));
}

/**
 * Cleans a shape ID by removing quotes and returns both clean and quoted versions
 * @param {string} shapeId - The shape ID (with or without quotes)
 * @returns {Object} Object with cleanShapeId and quotedShapeId
 */
function normalizeShapeId(shapeId) {
  const cleanShapeId = shapeId.replace(/^'|'$/g, '');
  const quotedShapeId = `'${cleanShapeId}'`;
  return { cleanShapeId, quotedShapeId };
}

/**
 * Normalizes a color by removing the # prefix if present
 * @param {string} color - The color (with or without #)
 * @returns {string} Color without # prefix
 */
function normalizeColor(color) {
  return color.replace('#', '');
}

// ============================================================================
// OVERLAY SHAPE ANNOTATIONS
// ============================================================================

/**
 * Creates or updates an overlay shape annotation
 * @param {string} shapeId - The shape ID (without quotes)
 * @param {string} overlayId - The overlay ID
 * @param {string} overlayName - The overlay name
 * @param {string} shapeName - The shape name
 * @returns {Object} The created or existing annotation object
 */
export function createOverlayShapeAnnotation(shapeId, overlayId, overlayName, shapeName) {
  const annotations = getAnnotations();
  const { cleanShapeId, quotedShapeId } = normalizeShapeId(shapeId);

  // Check if annotation already exists
  const existingIndex = annotations.findIndex(
    a => a.Type === 'Overlay' && a.Match.Substring === quotedShapeId
  );

  if (existingIndex === -1) {
    const annotation = {
      Type: 'Overlay',
      Match: {
        Substring: quotedShapeId
      },
      Attributes: {
        Shape: cleanShapeId,
        Overlay: overlayId,
        OverlayName: overlayName,
        ShapeName: shapeName
      }
    };
    annotations.push(annotation);
    updateAnnotations(annotations);
    return annotation;
  } else {
    return annotations[existingIndex];
  }
}

/**
 * Removes an overlay shape annotation
 * @param {string} shapeId - The shape ID (with or without quotes)
 */
export function removeOverlayShapeAnnotation(shapeId) {
  const annotations = getAnnotations();
  const { quotedShapeId } = normalizeShapeId(shapeId);

  // Remove the annotation for this shape
  const filteredAnnotations = annotations.filter(
    a => !(a.Type === 'Overlay' && a.Match.Substring === quotedShapeId)
  );

  updateAnnotations(filteredAnnotations);
}

/**
 * Removes multiple overlay shape annotations
 * @param {Array<string>} shapeIds - Array of shape IDs to remove
 */
export function removeOverlayShapeAnnotations(shapeIds) {
  const annotations = getAnnotations();

  // Create a set of quoted shape IDs for efficient lookup
  const quotedShapeIds = new Set(
    shapeIds.map(id => normalizeShapeId(id).quotedShapeId)
  );

  // Remove annotations for all shapes in the list
  const filteredAnnotations = annotations.filter(
    a => !(a.Type === 'Overlay' && quotedShapeIds.has(a.Match.Substring))
  );

  updateAnnotations(filteredAnnotations);
}

// ============================================================================
// DRAWN SHAPE ANNOTATIONS
// ============================================================================

/**
 * Creates or updates a drawn shape annotation
 * @param {string} shapeDefinition - The shape definition (polygon or radius coordinates)
 * @param {string} label - The label/name for the shape
 * @param {string} color - The color (with or without #)
 * @param {Object} options - Optional flags, e.g. { radiusSearch: true } for circles from radius search modal
 */
export function createDrawnShapeAnnotation(shapeDefinition, label, color, options = {}) {
  const annotations = getAnnotations();

  // Check if annotation already exists for this shape
  const existingIndex = annotations.findIndex(
    a => a.Type === 'Shape' && a.Match.Substring === shapeDefinition
  );

  if (existingIndex === -1) {
    const annotation = {
      Type: 'Shape',
      Match: {
        Substring: shapeDefinition
      },
      Attributes: {
        Label: label,
        Color: normalizeColor(color)
      }
    };
    if (options.radiusSearch === true) {
      annotation.Attributes.RadiusSearch = true;
    }
    annotations.push(annotation);
  } else {
    // Update existing annotation
    annotations[existingIndex].Attributes.Label = label;
    annotations[existingIndex].Attributes.Color = normalizeColor(color);
    if (options.radiusSearch === true) {
      annotations[existingIndex].Attributes.RadiusSearch = true;
    }
  }

  updateAnnotations(annotations);
}

/**
 * Removes a drawn shape annotation
 * @param {string} shapeDefinition - The shape definition to remove
 */
export function removeDrawnShapeAnnotation(shapeDefinition) {
  const annotations = getAnnotations();

  // Remove the annotation for this shape
  const filteredAnnotations = annotations.filter(
    a => !(a.Type === 'Shape' && a.Match.Substring === shapeDefinition)
  );

  updateAnnotations(filteredAnnotations);
}

/**
 * Removes multiple drawn shape annotations
 * @param {Array<string>} shapeDefinitions - Array of shape definitions to remove
 */
export function removeDrawnShapeAnnotations(shapeDefinitions) {
  const annotations = getAnnotations();

  // Create a set of shape definitions for efficient lookup
  const shapeSet = new Set(shapeDefinitions);

  // Remove annotations for all shapes in the list
  const filteredAnnotations = annotations.filter(
    a => !(a.Type === 'Shape' && shapeSet.has(a.Match.Substring))
  );

  updateAnnotations(filteredAnnotations);
}

/**
 * Updates a drawn shape annotation's label
 * @param {string} shapeDefinition - The shape definition
 * @param {string} newLabel - The new label
 */
export function updateDrawnShapeLabel(shapeDefinition, newLabel) {
  const annotations = getAnnotations();

  const annotation = annotations.find(
    a => a.Type === 'Shape' && a.Match.Substring === shapeDefinition
  );

  if (annotation) {
    annotation.Attributes.Label = newLabel;
    updateAnnotations(annotations);
  }
}

/**
 * Updates a drawn shape annotation's color
 * @param {string} shapeDefinition - The shape definition
 * @param {string} newColor - The new color (with or without #)
 */
export function updateDrawnShapeColor(shapeDefinition, newColor) {
  const annotations = getAnnotations();

  const annotation = annotations.find(
    a => a.Type === 'Shape' && a.Match.Substring === shapeDefinition
  );

  if (annotation) {
    annotation.Attributes.Color = normalizeColor(newColor);
    updateAnnotations(annotations);
  }
}

// ============================================================================
// GENERAL ANNOTATION UTILITIES
// ============================================================================

/**
 * Clears all annotations
 */
export function clearAllAnnotations() {
  setState('annotations', []);
  sessionStorage.removeItem('annotations');
}

/**
 * Clears all annotations of a specific type
 * @param {string} type - The annotation type ('Overlay', 'Shape', etc.)
 */
export function clearAnnotationsByType(type) {
  const annotations = getAnnotations();
  const filteredAnnotations = annotations.filter(annotation => annotation.Type !== type);
  updateAnnotations(filteredAnnotations);
}
